import { useEffect, useState } from 'react';

import {
  getAdminKpiMetrics,
  getAdminRecentActivity,
  getAdminTopDeals,
  type AdminKpiMetrics,
  type AdminRecentActivityRow,
  type AdminTopDeal,
} from '@/lib/db/admin/kpi';

export type AdminDashboardState = {
  status: 'loading' | 'success' | 'error';
  metrics: AdminKpiMetrics | null;
  topDeals: AdminTopDeal[];
  activity: AdminRecentActivityRow[];
  error: Error | null;
};

const INITIAL: AdminDashboardState = {
  status: 'loading',
  metrics: null,
  topDeals: [],
  activity: [],
  error: null,
};

/**
 * Fetches the three dashboard datasets in parallel on mount. Activity
 * gracefully degrades to `[]` when the caller is admin (RLS returns no
 * rows) — that is a normal product state, not an error.
 */
export function useAdminDashboard(): AdminDashboardState {
  const [state, setState] = useState<AdminDashboardState>(INITIAL);

  useEffect(() => {
    let cancelled = false;
    setState(INITIAL);

    Promise.all([getAdminKpiMetrics(), getAdminTopDeals(5), getAdminRecentActivity(10)])
      .then(([metrics, topDeals, activity]) => {
        if (cancelled) return;
        setState({ status: 'success', metrics, topDeals, activity, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({
          status: 'error',
          metrics: null,
          topDeals: [],
          activity: [],
          error: err instanceof Error ? err : new Error(String(err)),
        });
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return state;
}
